import { useEffect, useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";
import { Card } from "./ui/card.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";
import { AsyncButton } from "./ui/async-button.js";

interface Policy {
  checkoutReminderMin: number;
  secondReminderMin: number;
  autoCloseAfterHours: number;
  correctionWindowDays: number;
  correctionMonthlyCap: number;
}

/** What the number is counted in, shown beside the box so nobody has to guess minutes from hours. */
type Unit = "minutes" | "hours" | "days" | "requests";

/** Each field, its bounds (the API's POLICY_LIMITS), its unit and what it means. */
const FIELDS: { key: keyof Policy; label: string; unit: Unit; hint: string; min: number; max: number }[] = [
  { key: "checkoutReminderMin", label: "1st reminder, after shift ends", unit: "minutes", hint: "Minutes after the shift's hours are up (check-in + shift length), so a late start gets a full shift.", min: 0, max: 720 },
  { key: "secondReminderMin", label: "2nd reminder, after shift ends", unit: "minutes", hint: "Minutes after the shift's hours are up. Must be later than the 1st reminder.", min: 1, max: 720 },
  { key: "autoCloseAfterHours", label: "Auto check-out, after check-in", unit: "hours", hint: "Hours after check-in. The day is closed as a missed check-out, with no hours until corrected.", min: 4, max: 36 },
  { key: "correctionWindowDays", label: "Correction window", unit: "days", hint: "How many days back an employee may request a correction. Never once that month's payroll is uploaded.", min: 1, max: 60 },
  { key: "correctionMonthlyCap", label: "Corrections allowed per month", unit: "requests", hint: "Per employee, per attendance month. HR can still correct beyond it.", min: 1, max: 31 },
];

/** "90 minutes" reads fine; "= 1 hr 30 min" is what an admin actually thinks in. */
function minutesInWords(total: number): string | null {
  if (!Number.isInteger(total) || total < 60) return null;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h} hr${h === 1 ? "" : "s"}${m ? ` ${m} min` : ""}`;
}

/**
 * The missed check-out policy: when the reminder goes out, when a forgotten check-out is
 * closed, and how corrections are limited. Super HR admin only; the route enforces it.
 */
export function AttendancePolicyCard() {
  const toast = useToast();
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [draft, setDraft] = useState<Record<keyof Policy, string>>({ checkoutReminderMin: "", secondReminderMin: "", autoCloseAfterHours: "", correctionWindowDays: "", correctionMonthlyCap: "" });
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get<Policy>("/admin/attendance-approval-setting")
      .then((r) => {
        setPolicy(r.data);
        setDraft(Object.fromEntries(FIELDS.map((f) => [f.key, String(r.data[f.key])])) as Record<keyof Policy, string>);
      })
      .catch((e) => { logError("AttendancePolicyCard.load", e); setErr("Could not load the policy."); });
  }, []);

  async function save() {
    const body: Partial<Policy> = {};
    for (const f of FIELDS) {
      const n = Number(draft[f.key]);
      if (!Number.isInteger(n) || n < f.min || n > f.max) {
        setErr(`${f.label}: a whole number from ${f.min} to ${f.max}`);
        throw new Error("validation");
      }
      body[f.key] = n;
    }
    if (body.secondReminderMin! <= body.checkoutReminderMin!) {
      setErr("The 2nd reminder must be later than the 1st.");
      throw new Error("validation");
    }
    setErr("");
    try {
      const r = await api.patch<Policy>("/admin/attendance-policy", body);
      setPolicy(r.data);
      toast.show("Missed check-out policy saved", "success");
    } catch (e) {
      logError("AttendancePolicyCard.save", e);
      setErr(errMsg(e) ?? "Could not save. The policy is unchanged.");
      throw e;
    }
  }

  return (
    <Card className="p-5">
      <p className="text-[14px] font-bold">Missed check-out</p>
      <p className="mt-1 text-[12px] text-surface-500">
        A check-in left open past the shift's hours is reminded twice, then closed as a missed check-out
        with no hours. The employee fixes it with a correction, which their manager or HR approves.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => {
          const inWords = f.unit === "minutes" ? minutesInWords(Number(draft[f.key])) : null;
          return (
            <div key={f.key}>
              <Label htmlFor={`policy-${f.key}`}>{f.label}</Label>
              <div className="flex items-center gap-3">
                <Input
                  id={`policy-${f.key}`} type="number" min={f.min} max={f.max} step={1}
                  value={draft[f.key]} disabled={!policy} className="flex-1"
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
                <span className="w-28 shrink-0 text-[12px] font-semibold text-surface-600">
                  {f.unit}{inWords && <span className="block font-normal text-surface-500">= {inWords}</span>}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-surface-500">{f.hint}</p>
            </div>
          );
        })}
      </div>
      {err && <p className="mt-3 text-[12px] text-error">{err}</p>}
      <div className="mt-4">
        <AsyncButton size="sm" disabled={!policy} onClick={() => save()}>Save</AsyncButton>
      </div>
    </Card>
  );
}
